import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../common/BackButton';
import {
  Save,
  AlertCircle,
  Package,
  DollarSign,
  Battery,
  ChevronRight,
  CheckCircle,
  Plus,
  Trash2,
  MapPin,
} from 'lucide-react';
import { batteriesAPI, batteryBrandsAPI, locationsAPI, madeInsAPI, zonesAPI } from '../../services/garageApi';

const CreateBattery = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});

  // Dropdown data
  const [batteryBrands, setBatteryBrands] = useState([]);
  const [locations, setLocations] = useState([]);
  const [madeIns, setMadeIns] = useState([]);

  // Distribution state
  const [totalQuantity, setTotalQuantity] = useState('');
  const [distributions, setDistributions] = useState([
    { location_id: '', zone_id: '', quantity: '' }
  ]);
  const [zonesCache, setZonesCache] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    // Product fields
    battery_name: '',
    battery_brand_id: '',
    made_in_id: '',
    date_received: '',
    cost_price: '',
    selling_price: '',
    tax_rate: '',
    is_tax_inclusive: false,
    notes: '',
    active: true,

    // Battery specific fields
    battery_shape: '',
    product_shape: '', // Alternative field name
    ampere: '',
    reversed: false,
    battery_type: '',
  });

  const steps = [
    { id: 0, name: 'Basic Information', icon: Package },
    { id: 1, name: 'Battery Specifications', icon: Battery },
    { id: 2, name: 'Pricing & Inventory', icon: DollarSign },
    { id: 3, name: 'Review & Submit', icon: CheckCircle }
  ];



  const extractData = (response) => {
    if (!response || !response.data) return [];

    if (Array.isArray(response.data)) {
      return response.data;
    }

    if (response.data.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }

    if (response.data.items && Array.isArray(response.data.items)) {
      return response.data.items;
    }

    return [];
  };

  let fetchData;
  fetchData = async () => {
    try {
      const [brandsRes, locationsRes, madeInsRes] = await Promise.all([
        batteryBrandsAPI.getAll(),
        locationsAPI.getAll(),
        madeInsAPI.getAll()
      ]);

      setBatteryBrands(extractData(brandsRes));
      setLocations(extractData(locationsRes));
      setMadeIns(extractData(madeInsRes));

    } catch (error) {
      console.error('Error fetching data:', error);
      setBatteryBrands([]);
      setLocations([]);
      setMadeIns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchZonesForLocation = useCallback(async (locationId) => {
    if (!locationId) return;
    if (zonesCache[locationId]) return;
    try {
      const response = await zonesAPI.getByLocation(locationId);
      const zonesData = response?.data?.data?.data || response?.data?.data || response?.data || [];
      setZonesCache(prev => ({ ...prev, [locationId]: Array.isArray(zonesData) ? zonesData : [] }));
    } catch (error) {
      setZonesCache(prev => ({ ...prev, [locationId]: [] }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zonesCache]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Build a set of used location/zone combination keys (excluding a given row index)
  const getUsedCombinations = (excludeIndex = -1) => {
    const used = new Set();
    distributions.forEach((d, i) => {
      if (i !== excludeIndex && d.location_id) {
        const key = `${d.location_id}-${d.zone_id || ''}`;
        used.add(key);
      }
    });
    return used;
  };

  // Check if a specific location/zone combo is already used in another row
  const isCombinationUsed = (locationId, zoneId, excludeIndex) => {
    const key = `${locationId}-${zoneId || ''}`;
    const used = getUsedCombinations(excludeIndex);
    return used.has(key);
  };

  // Distribution handlers
  const handleDistributionChange = (index, field, value) => {
    const updated = [...distributions];
    updated[index] = { ...updated[index], [field]: value };

    // If location changed, reset zone and fetch zones
    if (field === 'location_id') {
      updated[index].zone_id = '';
      if (value) fetchZonesForLocation(value);
    }

    // Check for duplicate combination after update
    const newLocId = updated[index].location_id;
    const newZoneId = updated[index].zone_id;
    if (newLocId) {
      const dupKey = `dist_${index}_duplicate`;
      const isDup = isCombinationUsed(newLocId, newZoneId, index);
      if (isDup) {
        setErrors(prev => ({ ...prev, [dupKey]: 'This location/zone combination has already been added to the distribution list.' }));
      } else {
        setErrors(prev => {
          const next = { ...prev };
          delete next[dupKey];
          distributions.forEach((_, otherIdx) => {
            if (otherIdx !== index) {
              const otherKey = `dist_${otherIdx}_duplicate`;
              if (next[otherKey]) {
                const otherLoc = updated[otherIdx]?.location_id;
                const otherZone = updated[otherIdx]?.zone_id;
                const otherCombo = `${otherLoc}-${otherZone || ''}`;
                const count = updated.filter((d, j) => j !== otherIdx && d.location_id && `${d.location_id}-${d.zone_id || ''}` === otherCombo).length;
                if (count === 0) delete next[otherKey];
              }
            }
          });
          return next;
        });
      }
    }

    setDistributions(updated);

    if (errors.distributions) {
      setErrors(prev => ({ ...prev, distributions: '' }));
    }
  };

  const addDistributionRow = () => {
    setDistributions(prev => [...prev, { location_id: '', zone_id: '', quantity: '' }]);
  };

  const removeDistributionRow = (index) => {
    if (distributions.length <= 1) return;
    const updated = distributions.filter((_, i) => i !== index);
    setDistributions(updated);

    setErrors(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        if (key.startsWith(`dist_${index}_`)) delete next[key];
      });
      Object.keys(next).forEach(key => {
        const match = key.match(/^dist_(\d+)_duplicate$/);
        if (match) delete next[key];
      });
      return next;
    });
  };

  const getAllocatedQuantity = () => {
    return distributions.reduce((sum, d) => sum + (parseInt(d.quantity) || 0), 0);
  };

  const getRemainingQuantity = () => {
    const total = parseInt(totalQuantity) || 0;
    return total - getAllocatedQuantity();
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch(step) {
      case 0:
        if (!formData.battery_name) newErrors.battery_name = 'Battery name is required';
        if (!formData.battery_brand_id) newErrors.battery_brand_id = 'Brand is required';
        if (!formData.made_in_id) newErrors.made_in_id = 'Origin is required';
        if (!formData.date_received) newErrors.date_received = 'Date received is required';
        break;
      case 1:
        if (!formData.battery_shape && !formData.product_shape) {
          newErrors.battery_shape = 'Battery shape is required';
        }
        if (!formData.ampere) newErrors.ampere = 'Ampere is required';
        if (!formData.battery_type) newErrors.battery_type = 'Battery type is required';
        break;
      case 2:
        if (!formData.cost_price) newErrors.cost_price = 'Cost price is required';
        if (!formData.selling_price) newErrors.selling_price = 'Selling price is required';
        if (!totalQuantity || parseInt(totalQuantity) < 1) {
          newErrors.total_quantity = 'Total quantity must be at least 1';
        }
        // Validate distributions only if user has filled in any row
        const hasAnyDistribution = distributions.some(d => d.location_id || d.quantity);
        if (hasAnyDistribution) {
          let hasDistError = false;
          const seenCombos = new Set();
          distributions.forEach((d, i) => {
            if (d.location_id || d.quantity) {
              if (!d.location_id) {
                newErrors[`dist_${i}_location`] = 'Location is required';
                hasDistError = true;
              }
              if (!d.quantity || parseInt(d.quantity) < 1) {
                newErrors[`dist_${i}_quantity`] = 'Quantity must be at least 1';
                hasDistError = true;
              }
              if (d.location_id) {
                const comboKey = `${d.location_id}-${d.zone_id || ''}`;
                if (seenCombos.has(comboKey)) {
                  newErrors[`dist_${i}_duplicate`] = 'This location/zone combination has already been added to the distribution list.';
                  hasDistError = true;
                }
                seenCombos.add(comboKey);
              }
            }
          });
          if (!hasDistError && totalQuantity && getRemainingQuantity() !== 0) {
            newErrors.distributions = `Allocated quantity (${getAllocatedQuantity()}) must equal total quantity (${totalQuantity})`;
          }
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handlePrevious = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(activeStep)) return;

    setSaving(true);
    try {
      // Prepare submit data matching the backend expectations
      const submitData = {
        // Battery product fields
        battery_name: formData.battery_name,
        battery_brand_id: formData.battery_brand_id,
        made_in_id: formData.made_in_id,
        battery_shape: formData.battery_shape || formData.product_shape,
        ampere: parseInt(formData.ampere) || 0,
        reversed: formData.reversed ? 1 : 0,
        battery_type: formData.battery_type,
        date_received: formData.date_received,
        cost_price: parseFloat(formData.cost_price) || 0,
        selling_price: parseFloat(formData.selling_price) || 0,
        tax_rate: parseFloat(formData.tax_rate) || 0,
        is_tax_inclusive: formData.is_tax_inclusive ? 1 : 0,
        notes: formData.notes || '',
        active: formData.active ? 1 : 0,

        // Inventory fields
        total_quantity: parseInt(totalQuantity) || 0,
      };

      // Send distributions only if user has filled in at least one row
      const filledDistributions = distributions.filter(d => d.location_id && d.quantity);
      if (filledDistributions.length > 0) {
        submitData.distributions = JSON.stringify(filledDistributions.map(d => ({
          location_id: d.location_id,
          zone_id: d.zone_id || null,
          quantity: parseInt(d.quantity),
        })));
      }

      await batteriesAPI.create(submitData);

      navigate('/batteries', {
        state: { message: 'Battery product created successfully!' }
      });
    } catch (error) {
      console.error('Error creating battery:', error);
      if (error.response?.data?.errors) {
        const errorMessages = Object.entries(error.response.data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n');
        setErrors({ submit: errorMessages });
      } else {
        setErrors({ submit: error.response?.data?.message || 'Failed to create battery product. Please try again.' });
      }
      setSaving(false);
    }
  };

  const calculateProfit = () => {
    const cost = parseFloat(formData.cost_price) || 0;
    const selling = parseFloat(formData.selling_price) || 0;
    const taxRate = parseFloat(formData.tax_rate) || 0;
    const profit = selling - cost;
    const margin = cost > 0 ? (profit / cost * 100).toFixed(2) : 0;
    const finalPrice = formData.is_tax_inclusive
        ? selling
        : selling * (1 + taxRate / 100);
    return { profit, margin, finalPrice };
  };

  if (loading) {
    return (
        <div className="flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
    );
  }

  return (
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <BackButton to="/batteries" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Add New Battery Product</h1>
              <p className="text-sm text-gray-600 mt-1">Create a new battery entry in your inventory</p>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                  <div key={step.id} className="flex-1">
                    <div className="flex items-center">
                      <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              activeStep >= index
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-200 text-gray-600'
                          }`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 ml-3">
                        <p className={`text-sm font-medium ${
                            activeStep >= index ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                          {step.name}
                        </p>
                      </div>
                      {index < steps.length - 1 && (
                          <ChevronRight className="w-5 h-5 text-gray-400 mx-2" />
                      )}
                    </div>
                  </div>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            {errors.submit && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span className="whitespace-pre-line">{errors.submit}</span>
                </div>
            )}

            {/* Step 0: Basic Information */}
            {activeStep === 0 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Battery Name <span className="text-red-500">*</span>
                      </label>
                      <input
                          type="text"
                          name="battery_name"
                          value={formData.battery_name}
                          onChange={handleChange}
                          placeholder="e.g., Amaron Pro 65D26L"
                          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.battery_name ? 'border-red-300' : 'border-gray-200'
                          }`}
                      />
                      {errors.battery_name && (
                          <p className="mt-1 text-sm text-red-600">{errors.battery_name}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Battery Brand <span className="text-red-500">*</span>
                      </label>
                      <select
                          name="battery_brand_id"
                          value={formData.battery_brand_id}
                          onChange={handleChange}
                          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.battery_brand_id ? 'border-red-300' : 'border-gray-200'
                          }`}
                      >
                        <option value="">Select Brand</option>
                        {batteryBrands.map(brand => (
                            <option key={brand.id} value={brand.id}>{brand.name}</option>
                        ))}
                      </select>
                      {errors.battery_brand_id && (
                          <p className="mt-1 text-sm text-red-600">{errors.battery_brand_id}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Made In <span className="text-red-500">*</span>
                      </label>
                      <select
                          name="made_in_id"
                          value={formData.made_in_id}
                          onChange={handleChange}
                          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.made_in_id ? 'border-red-300' : 'border-gray-200'
                          }`}
                      >
                        <option value="">Select Origin</option>
                        {madeIns.length > 0 ? (
                            madeIns.map(madeIn => (
                                <option key={madeIn.id} value={madeIn.id}>{madeIn.name}</option>
                            ))
                        ) : (
                            <option disabled>No origins available</option>
                        )}
                      </select>
                      {errors.made_in_id && (
                          <p className="mt-1 text-sm text-red-600">{errors.made_in_id}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date Received <span className="text-red-500">*</span>
                      </label>
                      <input
                          type="date"
                          name="date_received"
                          value={formData.date_received}
                          onChange={handleChange}
                          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.date_received ? 'border-red-300' : 'border-gray-200'
                          }`}
                      />
                      {errors.date_received && (
                          <p className="mt-1 text-sm text-red-600">{errors.date_received}</p>
                      )}
                    </div>

                    <div className="md:col-span-2 flex items-center">
                      <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            name="active"
                            checked={formData.active}
                            onChange={handleChange}
                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">
                      Active Product
                    </span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Add any additional notes..."
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
            )}

            {/* Step 1: Battery Specifications */}
            {activeStep === 1 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Battery Specifications</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Battery Shape <span className="text-red-500">*</span>
                      </label>
                      <select
                          name="battery_shape"
                          value={formData.battery_shape || formData.product_shape}
                          onChange={handleChange}
                          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.battery_shape ? 'border-red-300' : 'border-gray-200'
                          }`}
                      >
                        <option value="">Select Shape</option>
                        <option value="thin">Thin</option>
                        <option value="thick">Thick</option>
                      </select>
                      {errors.battery_shape && (
                          <p className="mt-1 text-sm text-red-600">{errors.battery_shape}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ampere (AH) <span className="text-red-500">*</span>
                      </label>
                      <input
                          type="number"
                          name="ampere"
                          value={formData.ampere}
                          onChange={handleChange}
                          placeholder="e.g., 65"
                          min="0"
                          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.ampere ? 'border-red-300' : 'border-gray-200'
                          }`}
                      />
                      {errors.ampere && (
                          <p className="mt-1 text-sm text-red-600">{errors.ampere}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Battery Type <span className="text-red-500">*</span>
                      </label>
                      <select
                          name="battery_type"
                          value={formData.battery_type}
                          onChange={handleChange}
                          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.battery_type ? 'border-red-300' : 'border-gray-200'
                          }`}
                      >
                        <option value="">Select Type</option>
                        <option value="gel">Gel</option>
                        <option value="acid">Acid</option>
                        <option value="lithium">Lithium</option>
                        <option value="agm">AGM</option>
                        <option value="efb">EFB</option>
                      </select>
                      {errors.battery_type && (
                          <p className="mt-1 text-sm text-red-600">{errors.battery_type}</p>
                      )}
                    </div>

                    <div className="flex items-center">
                      <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            name="reversed"
                            checked={formData.reversed}
                            onChange={handleChange}
                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">
                      Reversed Terminal
                    </span>
                      </label>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Additional Specifications</h4>
                    <div className="text-sm text-gray-600 space-y-2">
                      <p>- Ensure the battery specifications match the manufacturer's label</p>
                      <p>- Verify the ampere rating for proper vehicle compatibility</p>
                      <p>- Check terminal configuration (standard or reversed)</p>
                    </div>
                  </div>
                </div>
            )}

            {/* Step 2: Pricing & Inventory */}
            {activeStep === 2 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing & Inventory</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cost Price <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <DollarSign className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="number"
                            step="0.01"
                            name="cost_price"
                            value={formData.cost_price}
                            onChange={handleChange}
                            placeholder="0.00"
                            className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors.cost_price ? 'border-red-300' : 'border-gray-200'
                            }`}
                        />
                      </div>
                      {errors.cost_price && (
                          <p className="mt-1 text-sm text-red-600">{errors.cost_price}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Selling Price <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <DollarSign className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="number"
                            step="0.01"
                            name="selling_price"
                            value={formData.selling_price}
                            onChange={handleChange}
                            placeholder="0.00"
                            className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                errors.selling_price ? 'border-red-300' : 'border-gray-200'
                            }`}
                        />
                      </div>
                      {errors.selling_price && (
                          <p className="mt-1 text-sm text-red-600">{errors.selling_price}</p>
                      )}
                    </div>
                  </div>

                  {/* Tax Configuration */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tax Rate (%)
                      </label>
                      <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          name="tax_rate"
                          value={formData.tax_rate}
                          onChange={handleChange}
                          placeholder="0.00"
                          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.tax_rate ? 'border-red-300' : 'border-gray-200'
                          }`}
                      />
                      {errors.tax_rate && <p className="mt-1 text-sm text-red-600">{errors.tax_rate}</p>}
                    </div>
                    <div className="flex items-center">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            name="is_tax_inclusive"
                            checked={formData.is_tax_inclusive}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              is_tax_inclusive: e.target.checked
                            }))}
                            className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        <span className="ml-3 text-sm font-medium text-gray-700">Tax Inclusive</span>
                      </label>
                      <p className="ml-2 text-xs text-gray-500">
                        {formData.is_tax_inclusive
                            ? 'Selling price already includes tax'
                            : 'Tax will be added to selling price'}
                      </p>
                    </div>
                  </div>

                  {formData.cost_price && formData.selling_price && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-blue-900">Profit Margin</p>
                            <p className="text-2xl font-bold text-blue-900">
                              ${calculateProfit().profit.toFixed(2)}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-blue-900">Margin %</p>
                            <p className="text-2xl font-bold text-blue-900">
                              {calculateProfit().margin}%
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-blue-900">Final Price (incl. tax)</p>
                            <p className="text-xs text-blue-700 mt-0.5">
                              {formData.is_tax_inclusive
                                  ? 'Tax included in selling price'
                                  : `Selling Price + ${formData.tax_rate || 0}% tax`}
                            </p>
                            <p className="text-2xl font-bold text-green-700">
                              ${calculateProfit().finalPrice.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                  )}

                  {/* Stock Distribution */}
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Stock Distribution
                      </h4>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">
                      Optional: Specify where to store the stock. If left empty, all stock will be placed in the Unallocated zone for later distribution.
                    </p>

                    {/* Total Quantity */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Total Quantity <span className="text-red-500">*</span>
                      </label>
                      <input
                          type="number"
                          value={totalQuantity}
                          onChange={(e) => {
                            setTotalQuantity(e.target.value);
                            if (errors.total_quantity) setErrors(prev => ({ ...prev, total_quantity: '' }));
                          }}
                          placeholder="Enter total stock quantity"
                          min="1"
                          className={`w-full max-w-xs px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              errors.total_quantity ? 'border-red-300' : 'border-gray-200'
                          }`}
                      />
                      {errors.total_quantity && <p className="mt-1 text-sm text-red-600">{errors.total_quantity}</p>}
                    </div>

                    {/* Remaining Counter */}
                    {totalQuantity && parseInt(totalQuantity) > 0 && (
                        <div className={`mb-4 p-3 rounded-lg border ${
                            getRemainingQuantity() === 0
                                ? 'bg-green-50 border-green-200 text-green-700'
                                : getRemainingQuantity() < 0
                                    ? 'bg-red-50 border-red-200 text-red-700'
                                    : 'bg-yellow-50 border-yellow-200 text-yellow-700'
                        }`}>
                          <div className="flex items-center justify-between text-sm font-medium">
                            <span>Allocated: {getAllocatedQuantity()} / {totalQuantity}</span>
                            <span>
                              {getRemainingQuantity() === 0
                                  ? 'Fully allocated'
                                  : getRemainingQuantity() > 0
                                      ? `${getRemainingQuantity()} remaining`
                                      : `${Math.abs(getRemainingQuantity())} over-allocated`}
                            </span>
                          </div>
                          <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                            <div
                                className={`h-2 rounded-full transition-all ${
                                    getRemainingQuantity() === 0
                                        ? 'bg-green-500'
                                        : getRemainingQuantity() < 0
                                            ? 'bg-red-500'
                                            : 'bg-yellow-500'
                                }`}
                                style={{ width: `${Math.min(100, (getAllocatedQuantity() / parseInt(totalQuantity)) * 100)}%` }}
                            />
                          </div>
                        </div>
                    )}

                    {errors.distributions && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          {errors.distributions}
                        </div>
                    )}

                    {/* Distribution Rows */}
                    <div className="space-y-3">
                      {distributions.map((dist, index) => {
                        const usedCombos = getUsedCombinations(index);
                        const hasDuplicateError = !!errors[`dist_${index}_duplicate`];

                        return (
                            <div key={index} className={`flex items-start gap-3 p-4 rounded-lg border ${
                                hasDuplicateError ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                            }`}>
                              <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Location
                                </label>
                                <select
                                    value={dist.location_id}
                                    onChange={(e) => handleDistributionChange(index, 'location_id', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        errors[`dist_${index}_location`] || hasDuplicateError ? 'border-red-300' : 'border-gray-200'
                                    }`}
                                >
                                  <option value="">Select Location</option>
                                  {locations.map(loc => {
                                    const locationZones = zonesCache[String(loc.id)] || [];
                                    const noZoneUsed = usedCombos.has(`${loc.id}-`);
                                    const allZonesUsed = locationZones.length > 0
                                        ? locationZones.every(z => usedCombos.has(`${loc.id}-${z.id}`))
                                        : noZoneUsed;
                                    return (
                                        <option
                                            key={loc.id}
                                            value={String(loc.id)}
                                            disabled={allZonesUsed && String(loc.id) !== String(dist.location_id)}
                                        >
                                          {loc.name}{allZonesUsed && String(loc.id) !== String(dist.location_id) ? ' (all zones used)' : ''}
                                        </option>
                                    );
                                  })}
                                </select>
                                {errors[`dist_${index}_location`] && (
                                    <p className="mt-1 text-xs text-red-600">{errors[`dist_${index}_location`]}</p>
                                )}
                              </div>

                              <div className="flex-1">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Zone</label>
                                <select
                                    value={dist.zone_id}
                                    onChange={(e) => handleDistributionChange(index, 'zone_id', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        hasDuplicateError ? 'border-red-300' : 'border-gray-200'
                                    }`}
                                    disabled={!dist.location_id || !(zonesCache[dist.location_id]?.length > 0)}
                                >
                                  <option value="">
                                    {!dist.location_id
                                        ? 'Select location first'
                                        : zonesCache[dist.location_id]?.length > 0
                                            ? 'Select Zone (optional)'
                                            : 'No zones available'}
                                  </option>
                                  {(zonesCache[dist.location_id] || []).map(zone => {
                                    const zoneUsed = usedCombos.has(`${dist.location_id}-${zone.id}`);
                                    return (
                                        <option
                                            key={zone.id}
                                            value={String(zone.id)}
                                            disabled={zoneUsed}
                                        >
                                          {zone.name}{zoneUsed ? ' (already used)' : ''}
                                        </option>
                                    );
                                  })}
                                </select>
                                {hasDuplicateError && (
                                    <p className="mt-1 text-xs text-red-600">{errors[`dist_${index}_duplicate`]}</p>
                                )}
                              </div>

                              <div className="w-32">
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Qty
                                </label>
                                <input
                                    type="number"
                                    value={dist.quantity}
                                    onChange={(e) => handleDistributionChange(index, 'quantity', e.target.value)}
                                    placeholder="0"
                                    min="1"
                                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        errors[`dist_${index}_quantity`] ? 'border-red-300' : 'border-gray-200'
                                    }`}
                                />
                                {errors[`dist_${index}_quantity`] && (
                                    <p className="mt-1 text-xs text-red-600">{errors[`dist_${index}_quantity`]}</p>
                                )}
                              </div>

                              {distributions.length > 1 && (
                                  <button
                                      type="button"
                                      onClick={() => removeDistributionRow(index)}
                                      className="mt-6 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                              )}
                            </div>
                        );
                      })}
                    </div>

                    <button
                        type="button"
                        onClick={addDistributionRow}
                        className="mt-3 px-4 py-2 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Distribution Row
                    </button>
                  </div>
                </div>
            )}

            {/* Step 3: Review & Submit */}
            {activeStep === 3 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Review & Submit</h3>

                  <div className="bg-gray-50 rounded-lg p-6 space-y-6">
                    {/* Basic Information Review */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Basic Information</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Battery Name:</span>
                          <span className="ml-2 font-medium">{formData.battery_name}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Brand:</span>
                          <span className="ml-2 font-medium">
                        {batteryBrands.find(b => b.id === formData.battery_brand_id)?.name || 'N/A'}
                      </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Made In:</span>
                          <span className="ml-2 font-medium">
                        {madeIns.find(m => m.id === formData.made_in_id)?.name || 'N/A'}
                      </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Date Received:</span>
                          <span className="ml-2 font-medium">{formData.date_received || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Status:</span>
                          <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                              formData.active
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-700'
                          }`}>
                        {formData.active ? 'Active' : 'Inactive'}
                      </span>
                        </div>
                      </div>
                    </div>

                    {/* Specifications Review */}
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Specifications</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Shape:</span>
                          <span className="ml-2 font-medium capitalize">
                        {formData.battery_shape || formData.product_shape || 'N/A'}
                      </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Ampere:</span>
                          <span className="ml-2 font-medium">{formData.ampere} AH</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Type:</span>
                          <span className="ml-2 font-medium capitalize">{formData.battery_type}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Reversed:</span>
                          <span className="ml-2 font-medium">{formData.reversed ? 'Yes' : 'No'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Pricing Review */}
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Pricing & Inventory</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Cost Price:</span>
                          <span className="ml-2 font-medium">${formData.cost_price}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Selling Price:</span>
                          <span className="ml-2 font-medium">${formData.selling_price}</span>
                        </div>
                        {formData.tax_rate && (
                        <div>
                          <span className="text-gray-600">Tax Rate:</span>
                          <span className="ml-2 font-medium">{formData.tax_rate}% {formData.is_tax_inclusive ? '(Inclusive)' : '(Exclusive)'}</span>
                        </div>
                        )}
                        <div>
                          <span className="text-gray-600">Profit:</span>
                          <span className="ml-2 font-medium text-green-600">
                        ${calculateProfit().profit.toFixed(2)} ({calculateProfit().margin}%)
                      </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Final Price:</span>
                          <span className="ml-2 font-medium text-green-600">${calculateProfit().finalPrice.toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Total Quantity:</span>
                          <span className="ml-2 font-medium">{totalQuantity} units</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Total Value:</span>
                          <span className="ml-2 font-medium">
                        ${(parseFloat(formData.selling_price) * parseInt(totalQuantity || 0)).toFixed(2)}
                      </span>
                        </div>
                      </div>
                    </div>

                    {/* Distribution Review */}
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Stock Distribution</h4>
                      {distributions.some(d => d.location_id && d.quantity) ? (
                          <div className="space-y-2">
                            {distributions.filter(d => d.location_id && d.quantity).map((dist, index) => {
                              const loc = locations.find(l => String(l.id) === String(dist.location_id));
                              const zone = (zonesCache[dist.location_id] || []).find(z => String(z.id) === String(dist.zone_id));
                              return (
                                  <div key={index} className="flex items-center justify-between text-sm bg-white p-3 rounded-lg border border-gray-200">
                                    <div className="flex items-center gap-2">
                                      <MapPin className="w-4 h-4 text-gray-400" />
                                      <span className="font-medium">{loc?.name || 'Unknown'}</span>
                                      {zone && (
                                          <span className="text-gray-500">/ {zone.name}</span>
                                      )}
                                    </div>
                                    <span className="font-medium">{dist.quantity} units</span>
                                  </div>
                              );
                            })}
                          </div>
                      ) : (
                          <div className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 p-3 rounded-lg flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            All {totalQuantity} units will be placed in the Unallocated zone for later distribution.
                          </div>
                      )}
                    </div>
                  </div>

                  {formData.notes && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Notes</h4>
                        <p className="text-sm text-gray-700">{formData.notes}</p>
                      </div>
                  )}
                </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t">
              <button
                  type="button"
                  onClick={handlePrevious}
                  className={`px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 ${
                      activeStep === 0 ? 'invisible' : ''
                  }`}
              >
                Previous
              </button>

              <div className="flex items-center gap-3">
                {activeStep < steps.length - 1 ? (
                    <button
                        type="button"
                        onClick={handleNext}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                ) : (
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                      {saving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Saving...
                          </>
                      ) : (
                          <>
                            <Save className="w-4 h-4" />
                            Create Battery Product
                          </>
                      )}
                    </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
  );
};

export default CreateBattery;
